import React from "react";
import { BiImage, BiMicrophone, BiCodeAlt, BiLayer } from "@acme/ui";
import { FooterSite, HeaderSite, LandingFaq, LandingFeatures, LandingHero, LandingModelSupport } from "@acme/feature";
import Head from "next/head";

const AnthropicClaudeApiUiPage = () => {
    const features = [
        {
            title: "Massive Context",
            description: "Leverage Claude's 200k token context window to analyze large documents and codebases.",
            icon: <BiLayer className="text-3xl" />,
            colSpan: "md:col-span-2",
            bg: "bg-orange-50 dark:bg-orange-900/10 border-orange-100 dark:border-orange-800/20"
        },
        {
            title: "Vision Support",
            description: "Upload diagrams, charts, and photos for Claude to analyze.",
            icon: <BiImage className="text-3xl" />,
            colSpan: "md:col-span-1",
            bg: "bg-red-50 dark:bg-red-900/10 border-red-100 dark:border-red-800/20"
        },
        {
            title: "Natural Writing",
            description: "Experience Claude\u2019s superior creative writing and natural tone.",
            icon: <BiMicrophone className="text-3xl" />,
            colSpan: "md:col-span-1",
            bg: "bg-amber-50 dark:bg-amber-900/10 border-amber-100 dark:border-amber-800/20"
        },
        {
            title: "Prompt Library",
            description: "Save system prompts to steer Claude's behavior perfectly.",
            icon: <BiCodeAlt className="text-3xl" />,
            colSpan: "md:col-span-2",
            bg: "bg-yellow-50 dark:bg-yellow-900/10 border-yellow-100 dark:border-yellow-800/20"
        },
    ];

    const faqList = [
        { "question": "Can I use Claude Opus?", "answer": "Yes, DapperGPT supports the entire Claude family: Opus, Sonnet, and Haiku." },
        { "question": "How do I get an Anthropic API Key?", "answer": "You can sign up for an API key at console.anthropic.com." },
        { "question": "Is image upload supported with Claude?", "answer": "Yes, DapperGPT supports image uploads when your selected Claude model supports vision." },
        { "question": "Does this work as a Claude desktop UI?", "answer": "Yes. It\u2019s a clean, fast Claude UI in the browser with organization features like folders and search." },
        { "question": "Can I separate work and personal contexts?", "answer": "Yes. Use folders, pinned chats, and saved prompts to keep contexts separate." },
        {
            "question": "Do you proxy requests to Anthropic?", "answer": "No\u2014your API key is used directly from your device depending on your setup."
        }
    ]

    return (
        <>
            <Head>
                <title>UI for Anthropic Claude API - DapperGPT</title>
                <meta name="description" content="A modern UI for Anthropic Claude. Use Claude models with your own API key, organize conversations, and work faster with folders, search, and reusable prompts." />
                <meta property="og:title" content="UI for Anthropic Claude API" />
                <meta property="og:description" content="The best interface for Claude 3 & Anthropic API." />
            </Head>
            <div className="bg-white dark:bg-gray-900 min-h-screen flex flex-col font-sans text-gray-900 dark:text-gray-100 selection:bg-blue-500/30 transition-colors duration-300">
                <HeaderSite containerClassName="!max-w-7xl px-6" />

                <main className="flex-grow">
                    <LandingHero
                        title="UI for Anthropic Claude"
                        subtitle="Interact with Claude Opus, Sonnet, and Haiku in a premium interface. Upload files, analyze images, and chat with large context windows."
                        badge="Claude Interface"
                        heroImage="/page/anthropic-claude-api-ui.png"
                        color="orange"
                    />
                    <LandingFeatures
                        title="Why use DapperGPT for Claude?"
                        subtitle="A powerful, flexible workspace for Anthropic\u2019s diverse models."
                        features={features}
                    />
                    <LandingModelSupport title="Supported Anthropic Models">
                        <div className="flex items-center gap-2"><span className="text-2xl font-bold font-serif text-gray-900 dark:text-gray-100">Claude 3 Opus</span></div>
                        <div className="flex items-center gap-2"><span className="text-2xl font-bold text-gray-900 dark:text-gray-100">Claude 3 Sonnet</span></div>
                        <div className="flex items-center gap-2"><span className="text-2xl font-bold font-sans text-gray-900 dark:text-gray-100">Claude 3 Haiku</span></div>
                    </LandingModelSupport>
                    <LandingFaq items={faqList} />
                </main>

                <FooterSite />
            </div>
        </>
    );
};

export default AnthropicClaudeApiUiPage;
